import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import EditWarrantyDialogComponent from "./editWarrantyDialog.component";
import { updateConsumerWarranty } from "modules/warranty/redux";
import { showBackdrop, hideBackdrop } from "modules/backdrop";
import { getDate } from "lib/generalUtility";
import moment from "moment";

function EditWarrantyDialogContainer({ isOpen, handleClose }) {
  const dispatch = useDispatch();
  const consumerWarrantyDetail = useSelector(
    (state) => state.warranty.consumerWarrantyDetail
  );
  const lang = useSelector((state) => state.constant.languages);
  
  // Add isMounted ref to track component mount status
  const isMounted = useRef(true);
  
  // Set up cleanup on unmount
  useEffect(() => {
    return () => {
      // Before unmounting, ensure backdrop is hidden to prevent infinite loading
      dispatch(hideBackdrop());
      isMounted.current = false;
    };
  }, [dispatch]);

  // Store the date obj internally
  const [purchaseDateObj, setPurchaseDateObj] = useState(
    consumerWarrantyDetail.purchasedAt
      ? new Date(consumerWarrantyDetail.purchasedAt)
      : new Date()
  );

  // Convert to SRT format for the DatePicker component using the expected format
  const purchaseDate = purchaseDateObj ? moment(purchaseDateObj).format("DD MMM YYYY") : "";

  const onDateChange = (date) => {
    setPurchaseDateObj(date);
  };

  const onSubmit = async () => {
    try {
      await dispatch(showBackdrop());
      const selectedDate = purchaseDateObj instanceof Date ? purchaseDateObj : new Date(purchaseDateObj);

      const durationMonth = consumerWarrantyDetail.durationMonth || 0;
      const extMonth = consumerWarrantyDetail.extMonth || 0;
      const totalMonths = durationMonth + extMonth;

      // Convert purchase date to UTC midnight STR first
      const purchaseDateUTC = new Date(Date.UTC(
        selectedDate.getFullYear(),
        selectedDate.getMonth(),
        selectedDate.getDate(),
        0,
        0,
        0
      ));

      const startAtUTC = purchaseDateUTC;
      const purchaseISO = purchaseDateUTC.toISOString();

      const endDateLocal = new Date(selectedDate);
      endDateLocal.setMonth(endDateLocal.getMonth() + totalMonths);
      const endDateUTC = new Date(Date.UTC(
        endDateLocal.getFullYear(),
        endDateLocal.getMonth(),
        endDateLocal.getDate(),
        0,
        0,
        0
      ));

      const updatedMetadata = (consumerWarrantyDetail.metadata || []).map((m) =>
        m.key === "purchase_date" || m.key === "purchased_at"
          ? { ...m, value: getDate(purchaseDateUTC, true) }
          : m
      );
      // If no matching key found, append new key
      if (
        !updatedMetadata.some(
          (m) => m.key === "purchase_date" || m.key === "purchased_at"
        )
      ) {
        updatedMetadata.push({
          key: "purchase_date",
          value: getDate(purchaseDateUTC, true),
          type: "date",
        });
      }

      await dispatch(
        updateConsumerWarranty({
          id: consumerWarrantyDetail.id,
          purchasedAt: purchaseISO,
          startAt: startAtUTC.toISOString(),
          endAt: endDateUTC.toISOString(),
          metadata: updatedMetadata,
        })
      );
    } finally {
      // Always hide backdrop regardless of component mount status
      dispatch(hideBackdrop());
      
      // Only call close handler if component is still mounted
      if (isMounted.current) {
        handleClose();
      }
    }
  };

  return (
    <EditWarrantyDialogComponent
      isOpen={isOpen}
      handleClose={handleClose}
      purchaseDate={purchaseDate}
      onDateChange={onDateChange}
      handleSubmit={onSubmit}
      lang={lang}
    />
  );
}

export default EditWarrantyDialogContainer; 